import { ArrowLeft, Check, Eye, Search, Shield, Users as UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpSupportButton } from '@/components/help-support';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useUsersList } from '@/hooks/useUsersData';
import { DEFAULT_PERMISSIONS, ROLE_DESCRIPTIONS, type UserPermissions } from '@/types/user';

interface CustomRole {
  id: string;
  name: string;
  description: string;
  permissions: UserPermissions;
  userCount: number;
  isSystem: boolean;
  createdAt: string;
}

const SYSTEM_ROLES: CustomRole[] = [
  {
    id: 'administrator',
    name: 'Administrator',
    description: ROLE_DESCRIPTIONS.administrator,
    permissions: DEFAULT_PERMISSIONS.administrator,
    userCount: 0,
    isSystem: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'user',
    name: 'User',
    description: ROLE_DESCRIPTIONS.user,
    permissions: DEFAULT_PERMISSIONS.user,
    userCount: 0,
    isSystem: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
];

const PERMISSION_LABELS: Record<keyof UserPermissions, string> = {
  manageUsers: 'Manage Users',
  manageCompanies: 'Manage Companies',
  manageIntegrations: 'Manage Integrations',
  configureAutomation: 'Configure Automation',
  configureAI: 'Configure AI',
  viewReports: 'View Reports',
  manageAccounts: 'Manage Accounts',
  sendCommunications: 'Send Communications',
  logActivities: 'Log Activities',
  viewPayments: 'View Payments',
  allocatePayments: 'Allocate Payments',
  reverseAllocations: 'Reverse Allocations',
  customerAccountsView: 'Customer Accounts View',
  customerAccountsEdit: 'Customer Accounts Edit',
  customerAccountsReassign: 'Customer Accounts Reassign',
  collectionCasesView: 'Collection Cases View',
  collectionCasesCreate: 'Collection Cases Create',
  collectionCasesEdit: 'Collection Cases Edit',
  collectionCasesDelete: 'Collection Cases Delete',
  collectionCasesReassign: 'Collection Cases Reassign',
  promiseToPayView: 'Promise to Pay View',
  promiseToPayCreate: 'Promise to Pay Create',
  promiseToPayEdit: 'Promise to Pay Edit',
  promiseToPayDelete: 'Promise to Pay Delete',
  promiseToPayReassign: 'Promise to Pay Reassign',
  viewCreditControlDashboard: 'View Credit Control Dashboard',
  viewCollectionsDashboard: 'View Collections Dashboard',
  viewDisputesDashboard: 'View Disputes Dashboard',
  viewAgeingAnalysis: 'View Ageing Analysis',
  viewMultiCompanyDashboard: 'View Multi-Company Dashboard',
  accessManagerViews: 'Access Manager Views',
  adminManageUsersView: 'Admin: Manage Users - View',
  adminManageUsersCreate: 'Admin: Manage Users - Create',
  adminManageUsersEdit: 'Admin: Manage Users - Edit',
  adminManageUsersDelete: 'Admin: Manage Users - Delete',
  adminManageCompaniesView: 'Admin: Manage Companies - View',
  adminManageCompaniesCreate: 'Admin: Manage Companies - Create',
  adminManageCompaniesEdit: 'Admin: Manage Companies - Edit',
  adminManageCompaniesDelete: 'Admin: Manage Companies - Delete',
  adminManageGeneralSettingsView: 'Admin: Manage General Settings - View',
  adminManageGeneralSettingsCreate: 'Admin: Manage General Settings - Create',
  adminManageGeneralSettingsEdit: 'Admin: Manage General Settings - Edit',
  adminManageGeneralSettingsDelete: 'Admin: Manage General Settings - Delete',
  adminManageIntegrationsView: 'Admin: Manage Integrations - View',
  adminManageIntegrationsCreate: 'Admin: Manage Integrations - Create',
  adminManageIntegrationsEdit: 'Admin: Manage Integrations - Edit',
  adminManageIntegrationsDelete: 'Admin: Manage Integrations - Delete',
  adminManageTemplatesView: 'Admin: Manage Templates - View',
  adminManageTemplatesCreate: 'Admin: Manage Templates - Create',
  adminManageTemplatesEdit: 'Admin: Manage Templates - Edit',
  adminManageTemplatesDelete: 'Admin: Manage Templates - Delete',
  adminManageCollectionStrategiesView: 'Admin: Manage Collection Strategies - View',
  adminManageCollectionStrategiesCreate: 'Admin: Manage Collection Strategies - Create',
  adminManageCollectionStrategiesEdit: 'Admin: Manage Collection Strategies - Edit',
  adminManageCollectionStrategiesDelete: 'Admin: Manage Collection Strategies - Delete',
  adminManageDisputeTypesView: 'Admin: Manage Dispute Types - View',
  adminManageDisputeTypesCreate: 'Admin: Manage Dispute Types - Create',
  adminManageDisputeTypesEdit: 'Admin: Manage Dispute Types - Edit',
  adminManageDisputeTypesDelete: 'Admin: Manage Dispute Types - Delete',
  adminConfigureAIView: 'Admin: Configure AI - View',
  adminConfigureAICreate: 'Admin: Configure AI - Create',
  adminConfigureAIEdit: 'Admin: Configure AI - Edit',
  adminConfigureAIDelete: 'Admin: Configure AI - Delete',
};

const PERMISSION_DESCRIPTIONS: Record<keyof UserPermissions, string> = {
  manageUsers: 'Create, edit, and delete user accounts',
  manageCompanies: 'Manage company settings and configurations',
  manageIntegrations: 'Configure and manage system integrations',
  configureAutomation: 'Set up and modify automation rules',
  configureAI: 'Configure AI and machine learning settings',
  viewReports: 'Access and view system reports',
  manageAccounts: 'Manage customer accounts and data',
  sendCommunications: 'Send emails and communications to customers',
  logActivities: 'Log activities and interactions',
  viewPayments: 'View payment records and transaction history',
  allocatePayments: 'Allocate payments to invoices',
  reverseAllocations: 'Reverse previously allocated payments',
  customerAccountsView: 'View customer accounts',
  customerAccountsEdit: 'Edit customer accounts',
  customerAccountsReassign: 'Reassign customer accounts',
  collectionCasesView: 'View collection cases',
  collectionCasesCreate: 'Create collection cases',
  collectionCasesEdit: 'Edit collection cases',
  collectionCasesDelete: 'Delete collection cases',
  collectionCasesReassign: 'Reassign collection cases',
  promiseToPayView: 'View promises to pay',
  promiseToPayCreate: 'Create promises to pay',
  promiseToPayEdit: 'Edit promises to pay',
  promiseToPayDelete: 'Delete promises to pay',
  promiseToPayReassign: 'Reassign promises to pay',
  viewCreditControlDashboard: 'Access to main credit control overview and metrics',
  viewCollectionsDashboard: 'Access to collections queue and team performance metrics',
  viewDisputesDashboard: 'Access to dispute tracking and analytics',
  viewAgeingAnalysis: 'Access to accounts receivable ageing reports',
  viewMultiCompanyDashboard: 'Access to consolidated multi-company view',
  accessManagerViews: 'Can switch between user views and access manager-level functionality',
  adminManageUsersView: 'View user account management',
  adminManageUsersCreate: 'Create new user accounts',
  adminManageUsersEdit: 'Edit existing user accounts',
  adminManageUsersDelete: 'Delete user accounts',
  adminManageCompaniesView: 'View company settings',
  adminManageCompaniesCreate: 'Create new companies',
  adminManageCompaniesEdit: 'Edit company configurations',
  adminManageCompaniesDelete: 'Delete companies',
  adminManageGeneralSettingsView: 'View general system settings',
  adminManageGeneralSettingsCreate: 'Create general settings',
  adminManageGeneralSettingsEdit: 'Edit general settings',
  adminManageGeneralSettingsDelete: 'Delete general settings',
  adminManageIntegrationsView: 'View system integrations',
  adminManageIntegrationsCreate: 'Create new integrations',
  adminManageIntegrationsEdit: 'Edit integration configurations',
  adminManageIntegrationsDelete: 'Delete integrations',
  adminManageTemplatesView: 'View communication templates',
  adminManageTemplatesCreate: 'Create new templates',
  adminManageTemplatesEdit: 'Edit existing templates',
  adminManageTemplatesDelete: 'Delete templates',
  adminManageCollectionStrategiesView: 'View collection strategies',
  adminManageCollectionStrategiesCreate: 'Create new strategies',
  adminManageCollectionStrategiesEdit: 'Edit strategy configurations',
  adminManageCollectionStrategiesDelete: 'Delete strategies',
  adminManageDisputeTypesView: 'View dispute type definitions',
  adminManageDisputeTypesCreate: 'Create new dispute types',
  adminManageDisputeTypesEdit: 'Edit dispute type definitions',
  adminManageDisputeTypesDelete: 'Delete dispute types',
  adminConfigureAIView: 'View AI configuration settings',
  adminConfigureAICreate: 'Create AI configurations',
  adminConfigureAIEdit: 'Edit AI settings',
  adminConfigureAIDelete: 'Delete AI configurations',
};

export function RolesPermissionsPage() {
  const navigate = useNavigate();
  const { data: users = [] } = useUsersList();
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate user counts for system roles
  const rolesWithCounts = useMemo(() => {
    const roleCounts = users.reduce(
      (acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return SYSTEM_ROLES.map((role) => ({
      ...role,
      userCount: roleCounts[role.id] || 0,
    }));
  }, [users]);

  const filteredRoles = useMemo(() => {
    if (!searchQuery) return rolesWithCounts;
    const query = searchQuery.toLowerCase();
    return rolesWithCounts.filter(
      (role) => role.name.toLowerCase().includes(query) || role.description.toLowerCase().includes(query),
    );
  }, [rolesWithCounts, searchQuery]);

  const handleViewRole = (role: CustomRole) => {
    // Navigate to role details page
    navigate(`/settings/users/roles/${role.id}`);
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Roles & Permissions</h1>
            <p className="text-muted-foreground mt-2">
              Define user roles and assign granular permissions to control access
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/settings/users')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
            <HelpSupportButton screenId="settings-user-management" />
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Roles Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredRoles.map((role) => (
            <Card key={role.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      {role.name}
                    </CardTitle>
                    {role.isSystem && (
                      <Badge variant="secondary" className="text-xs">
                        Platform Role
                      </Badge>
                    )}
                  </div>
                </div>
                <CardDescription className="line-clamp-2">{role.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <UsersIcon className="w-4 h-4" />
                  <span>
                    {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
                  </span>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Key Permissions:</p>
                  <div className="space-y-1">
                    {Object.entries(role.permissions)
                      .filter(([_, enabled]) => enabled)
                      .slice(0, 3)
                      .map(([key]) => (
                        <div key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-3 h-3 text-green-600" />
                          <span>{PERMISSION_LABELS[key as keyof UserPermissions]}</span>
                        </div>
                      ))}
                    {Object.values(role.permissions).filter(Boolean).length > 3 && (
                      <p className="text-xs text-muted-foreground">
                        +{Object.values(role.permissions).filter(Boolean).length - 3} more
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleViewRole(role)}>
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredRoles.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No roles found</h3>
            <p className="text-muted-foreground">Try adjusting your search or create a new role</p>
          </div>
        )}
      </div>
    </div>
  );
}
